import React, { forwardRef, useEffect, useRef } from "react";
import style from "./TextInput.module.css";

export interface TextInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onSubmit?: () => void;
  onClick?: (e: React.MouseEvent) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onMouseDown?: (e: React.MouseEvent<HTMLInputElement>) => void;
  onSelectionChange?: (e: Event) => void;
  className?: string;

  inputId?: string;
  inputAutofocus?: boolean;
  type?: "text" | "number" | "email" | "password" | "tel" | "url";
  placeholder?: string;
  name?: string;
  disabled?: boolean;
  required?: boolean;
  autocomplete?: string; // https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete, https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill
  
  // for 'email', and 'tel' and 'url' and maybe 'password'?
  pattern?: string;
  
  // for non-number types, how many characters can the input be?
  minLength?: number;
  maxLength?: number;

  // min/max and step values for type='number'
  min?: number;
  max?: number;
  step?: string;

  isReadonly?: boolean;
  invalid?: boolean;

  prefix?: React.ReactNode;
  showPrefixWithPlaceholder?: boolean;
  suffix?: React.ReactNode;
  borderless?: boolean;
  centerText?: boolean; // put the text in the middle of the input instead of the beginning. 
                        // useful for our number input with the +/- buttons on either side.
  xButtonToClear?: boolean; // Display an "X" at the right that you can click to clear the text
}

export default forwardRef(function TextInput(props: TextInputProps, forwardRef: React.ForwardedRef<HTMLInputElement>) {
  const {
    type = "text",
    name,
    placeholder,
    min,
    max,
    step,
    required,
    minLength,
    maxLength,
    pattern,
    disabled,
    autocomplete,
    value,
    inputId,
    inputAutofocus,
    invalid = false,
    isReadonly = false,
    prefix,
    showPrefixWithPlaceholder = true,
    suffix,
    borderless,
    centerText,
    className,
    onChange,
    onClick,
    onSubmit,
    onBlur,
    onFocus,
    onKeyDown,
    onMouseDown,
    onSelectionChange,
    xButtonToClear,
  } = props;

  const ref = useRef<HTMLInputElement>(null);
  const updateRefs = (el: HTMLInputElement) => {
    ref.current = el;
    if (forwardRef) {
      if (forwardRef instanceof Function) {
        forwardRef(el);
      } else {
        forwardRef.current = el;
      }
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !!onSubmit) onSubmit();
    onKeyDown?.(e);
  };

  useEffect(() => {
    if (ref.current && onSelectionChange) {
      const f = e => onSelectionChange(e);
      ref.current.addEventListener("selectionchange", f);
      return () => ref.current?.removeEventListener("selectionchange", f);
    }
  }, [ref.current, onSelectionChange]);

  return (
    <div
      className={`${style.inputContainer} ${className ? className : ""} ${invalid ? style.inputContainerInvalid : ''} ${isReadonly ? style.inputContainerReadonly : ''} ${disabled ? style.inputContainerDisabled : '' } ${isReadonly ? style.inputContainerReadonly : '' } ${borderless ? style.borderless : ''}`}
      onClick={e => {
        e.stopPropagation(); //Ensures click events don't pass through the element.
        onClick?.(e)
      }}
    >
      {!!prefix && (!!value || !placeholder || showPrefixWithPlaceholder) && <span className={style.prefix}>{prefix}</span>}
      <input
        ref={updateRefs}
        id={inputId}
        className={`${style.input} ${centerText ? style.inputCenter : ''}`}
        autoFocus={inputAutofocus}
        type={type}
        name={name}
        placeholder={placeholder}
        min={min}
        max={max}
        step={step}
        required={required}
        maxLength={maxLength}
        minLength={minLength}
        pattern={pattern}
        disabled={disabled}
        aria-disabled={disabled}
        readOnly={isReadonly}
        autoComplete={autocomplete}
        value={value}
        onKeyDown={handleKeyDown}
        onChange={e => {
          // @TODO, consider filtering input based on 'type'...
          onChange?.(e.target.value);
        }}
        onBlur={e => onBlur?.(e)}
        onFocus={e => onFocus?.(e)}
        onMouseDown={e => onMouseDown?.(e)}
      />
      {xButtonToClear && !!(ref.current?.value.length) &&
        <div style={{display: "flex",
                    /*@NOTE(michaelp): Padding & negative margin makes bigger clickbox*/
                    margin: "-15px",
                    padding: "15px",
                    cursor: "pointer"}}
             onClick={()=>{ ref.current.value = ""; onChange(ref.current.value); }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none"> <path d="M4.8 14.25L3.75 13.2L7.95 9L3.75 4.8L4.8 3.75L9 7.95L13.2 3.75L14.25 4.8L10.05 9L14.25 13.2L13.2 14.25L9 10.05L4.8 14.25Z" fill="#767676"/> </svg>
        </div>
      }
      {!!suffix && <span className={style.suffix}>{suffix}</span>}
    </div>
  );
});
